// libs
import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin } from 'prosemirror-state';

// types
import { EditorOptions, EditorView } from '@lyearn/lygo-core';

// utils
import { removeMenuContainer, run, setPluginMeta } from '../../utils';
import {
  EVENT_KEYS,
  MENTIONS_CONTAINER_ID,
  MENTIONS_CONTAINER_SELECTOR,
  MENTIONS_OPEN_REGEX,
  MENTIONS_PLUGIN_KEY,
} from './consts';
// components
import MentionsPopover from './MentionsPopover';
import { MentionsPluginProps, MentionsPluginState } from './types';

const handleOpening = (editorView: EditorView, prevProps: MentionsPluginState) => {
  // timeout ensures that the add/delete has been handled by prosemirror
  // and any characters removed, before we evaluate the rule.
  setTimeout(() => {
    const { pos } = editorView.state.selection.$from;
    return run(editorView, pos, pos, MENTIONS_OPEN_REGEX, (_state: any, match: any) => {
      const state: MentionsPluginState = {
        open: false,
        editorView,
        match: '',
        UserTooltipProvider: prevProps.UserTooltipProvider,
      };

      try {
        if (match) {
          state.open = true;
          state.editorView = editorView;
          state.match = match[0].split('@')[1];
        }
        setPluginMeta(editorView, MENTIONS_PLUGIN_KEY, state);
      } catch (error) {
        console.log({ error });
      }

      return null;
    });
  });
};

const getMentionsPlugin = (itemProps: MentionsPluginProps) => {
  return ({ portalApi }: EditorOptions) => {
    return [
      new Plugin({
        key: MENTIONS_PLUGIN_KEY,
        state: {
          init() {
            return {
              open: false,
              UserTooltipProvider: itemProps.UserTooltipProvider,
            };
          },
          apply(tr, old) {
            const props = tr.getMeta(MENTIONS_PLUGIN_KEY);
            if (props) return props;
            return old;
          },
        },
        view: (editorView) => {
          const portalKey = nanoid();

          // This will be called only once. It will add a container div.
          // Mentions Menu will be rendered in this container div.
          const mentionPopoverContainer = document.createElement('div');
          mentionPopoverContainer.id = MENTIONS_CONTAINER_ID;
          editorView.dom?.parentNode?.appendChild(mentionPopoverContainer);

          return {
            update: (view) => {
              const props = MENTIONS_PLUGIN_KEY.getState(view.state);
              const dom = view.dom?.parentNode?.querySelector(MENTIONS_CONTAINER_SELECTOR);

              if (dom) {
                portalApi.createPortal(
                  ReactDOM.createPortal(
                    <MentionsPopover {...props} ListComponent={itemProps.ListComponent} />,
                    dom,
                    portalKey,
                  ),
                );
              }
            },
            destroy: () => {
              portalApi.destroyPortal(portalKey);
              removeMenuContainer(MENTIONS_CONTAINER_ID, editorView);
            },
          };
        },

        props: {
          handleDOMEvents: {
            //handles the case when mentions menu is triggered from shortcut menu
            focus: (editorView, _event) => {
              const prevProps = MENTIONS_PLUGIN_KEY.getState(editorView.state);

              // if closed via click away listener, keep the popup closed on the focus event that follows it
              if (prevProps.terminated) {
                const state: MentionsPluginState = {
                  open: false,
                  editorView,
                  match: '',
                  UserTooltipProvider: itemProps.UserTooltipProvider,
                };
                setPluginMeta(editorView, MENTIONS_PLUGIN_KEY, state);
                return false;
              }
              handleOpening(editorView, prevProps);
              return false;
            },
            mousedown: (editorView) => {
              const state: MentionsPluginState = {
                open: false,
                editorView,
                match: '',
                UserTooltipProvider: itemProps.UserTooltipProvider,
              };
              setPluginMeta(editorView, MENTIONS_PLUGIN_KEY, state);
              return false;
            },
            keydown: (editorView, event) => {
              const prevProps = MENTIONS_PLUGIN_KEY.getState(editorView.state);
              // Ignore this keypresses here. It will be handled in the MentionsPopover.
              if (prevProps.open === true && EVENT_KEYS.includes(event.key)) {
                return true;
              }

              handleOpening(editorView, {
                ...prevProps,
                UserTooltipProvider: itemProps.UserTooltipProvider,
              });
              return false;
            },
          },
        },
      }),
    ];
  };
};

export { MENTIONS_PLUGIN_KEY };
export default getMentionsPlugin;
